'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Briefcase,
  Users,
  Heart,
  Rocket,
  Award,
  Coffee,
  Home,
  GraduationCap,
  Calendar,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Building2,
  Code2,
  Brain
} from 'lucide-react'
import Link from 'next/link'

import { FadeUp } from '@/components/ui/fade-up'
const values = [
  {
    icon: Heart,
    title: '地域愛',
    description: '地域の課題を自分事として捉え、持続可能な解決策を追求する情熱'
  },
  {
    icon: Rocket,
    title: '実装主義',
    description: '概念だけでなく、実際に動くシステムとして社会実装することへのこだわり'
  },
  {
    icon: Users,
    title: 'チームワーク',
    description: '多様な専門性を持つメンバーが協力し、最高の成果を生み出す文化'
  },
  {
    icon: Brain,
    title: '継続的学習',
    description: '最新技術を学び続け、地域に最適な形で活用する探究心'
  }
]

const benefits = [
  {
    icon: Home,
    title: 'リモートワーク',
    description: 'フルリモート可。広島オフィスでの勤務も選択可能'
  },
  {
    icon: Coffee,
    title: 'フレックスタイム',
    description: 'コアタイムなしの完全フレックス制度'
  },
  {
    icon: GraduationCap,
    title: '学習支援',
    description: '書籍購入、研修参加、資格取得を全額支援'
  },
  {
    icon: Award,
    title: '成果報酬',
    description: 'プロジェクトの成功に応じたインセンティブ制度'
  }
]

const positions = {
  engineering: [
    {
      id: 'frontend-engineer',
      title: 'フロントエンドエンジニア',
      type: '正社員',
      location: 'リモート可',
      description: 'Next.js/React/TypeScriptを用いた地域DXプロダクトの開発',
      requirements: [
        'React/TypeScriptでの開発経験3年以上',
        'UI/UXへの強い関心',
        'アクセシビリティへの理解'
      ],
      preferred: [
        'Next.js App Routerの経験',
        'Cloudflare Workersの経験',
        '地域課題への関心'
      ]
    },
    {
      id: 'backend-engineer',
      title: 'バックエンドエンジニア',
      type: '正社員',
      location: 'リモート可',
      description: 'PostgreSQL/Supabaseを活用したスケーラブルなシステム開発',
      requirements: [
        'サーバーサイド開発経験3年以上',
        'データベース設計の経験',
        'セキュリティへの理解'
      ],
      preferred: [
        'Supabase/PostgreSQLの経験',
        'マイクロサービスアーキテクチャの知識',
        'パフォーマンスチューニング経験'
      ]
    },
    {
      id: 'ai-engineer',
      title: 'AIエンジニア',
      type: '正社員',
      location: 'リモート可',
      description: '生成AI/機械学習を活用した地域課題解決ソリューションの開発',
      requirements: [
        'Python/機械学習フレームワークの経験',
        'LLM/生成AIの実装経験',
        'データ分析スキル'
      ],
      preferred: [
        'プロンプトエンジニアリングの経験',
        'エッジAIの知識',
        '日本語処理の経験'
      ]
    }
  ],
  business: [
    {
      id: 'dx-consultant',
      title: 'DXコンサルタント',
      type: '正社員',
      location: '広島/リモート',
      description: '自治体・地域企業のDX推進を戦略から実装まで支援',
      requirements: [
        'IT業界での経験3年以上',
        'プロジェクトマネジメント経験',
        'コミュニケーション能力'
      ],
      preferred: [
        '自治体/公共分野の経験',
        'DX推進の実績',
        '地域への思い'
      ]
    },
    {
      id: 'sales-manager',
      title: 'セールスマネージャー',
      type: '正社員',
      location: '広島/東京',
      description: '地域DXソリューションの提案営業とパートナーシップ構築',
      requirements: [
        'B2B営業経験5年以上',
        'SaaS/IT製品の営業経験',
        '提案力・交渉力'
      ],
      preferred: [
        '自治体営業の経験',
        'パートナー開拓の実績',
        'マネジメント経験'
      ]
    }
  ],
  design: [
    {
      id: 'ux-designer',
      title: 'UXデザイナー',
      type: '正社員/業務委託',
      location: 'リモート可',
      description: '地域の人々に寄り添うプロダクトのUX設計',
      requirements: [
        'UXデザイン経験3年以上',
        'ユーザーリサーチの経験',
        'プロトタイピングスキル'
      ],
      preferred: [
        '公共サービスのデザイン経験',
        'アクセシビリティの知識',
        'デザインシステムの構築経験'
      ]
    }
  ]
}

export function RecruitContent() {
  return (
    <>
      {/* ヒーローセクション */}
      <section className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-background py-16">
        <div
          className="animate-fade-up container"
        >
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4">Join Our Team</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              地域の未来を、共に創る
            </h1>
            <p className="text-xl text-muted-foreground">
              CodeFoxでは、地域のデジタル変革に情熱を持つ仲間を募集しています。<br />
              広島から全国へ、テクノロジーで地域課題を解決しませんか。
            </p>
          </div>
        </div>
      </section>

      {/* 文化・価値観 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">私たちが大切にしていること</h2>
            <p className="text-lg text-muted-foreground">
              CodeFoxで働く仲間が共有する価値観
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon
              return (
                <FadeUp delay={index * 0.1}
                  key={index}
                >
                  <Card className="h-full text-center">
                    <CardHeader>
                      <Icon className="h-12 w-12 text-primary mx-auto mb-4" />
                      <CardTitle>{value.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{value.description}</p>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* 働く環境 */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">働きやすい環境づくり</h2>
              <p className="text-lg text-muted-foreground">
                メンバーが最高のパフォーマンスを発揮できる環境を整えています
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon
                return (
                  <FadeUp delay={index * 0.1}
                    key={index}
                    className="flex gap-4"
                  >
                    <div className="shrink-0">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{benefit.title}</h3>
                      <p className="text-muted-foreground">{benefit.description}</p>
                    </div>
                  </FadeUp>
                )
              })}
            </div>

            <div className="mt-12 p-6 bg-primary/5 rounded-lg text-center">
              <p className="text-lg font-semibold mb-2">その他の福利厚生</p>
              <p className="text-muted-foreground">
                社会保険完備 / 交通費支給 / リモートワーク手当 / 
                健康診断 / インフルエンザ予防接種 / 懇親会補助
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* 募集職種 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">募集職種</h2>
            <p className="text-lg text-muted-foreground">
              あなたの専門性を地域のために活かしませんか
            </p>
          </FadeUp>

          <Tabs defaultValue="engineering" className="max-w-6xl mx-auto">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="engineering">
                <Code2 className="h-4 w-4 mr-2" />
                エンジニア
              </TabsTrigger>
              <TabsTrigger value="business">
                <Briefcase className="h-4 w-4 mr-2" />
                ビジネス
              </TabsTrigger>
              <TabsTrigger value="design">
                <Sparkles className="h-4 w-4 mr-2" />
                デザイン
              </TabsTrigger>
            </TabsList>

            {Object.entries(positions).map(([category, jobs]) => (
              <TabsContent key={category} value={category} className="mt-8">
                <div className="grid gap-6">
                  {jobs.map((job, index) => (
                    <FadeUp delay={index * 0.1}
                      key={job.id}
                    >
                      <Card>
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle className="text-2xl">{job.title}</CardTitle>
                              <CardDescription className="mt-2">
                                {job.description}
                              </CardDescription>
                            </div>
                            <div className="flex gap-2">
                              <Badge>{job.type}</Badge>
                              <Badge variant="secondary">
                                <MapPin className="h-3 w-3 mr-1" />
                                {job.location}
                              </Badge>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid md:grid-cols-2 gap-6">
                            <div>
                              <h4 className="font-semibold mb-3">必須スキル・経験</h4>
                              <ul className="space-y-2">
                                {job.requirements.map((req, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm">
                                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                    <span>{req}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h4 className="font-semibold mb-3">歓迎スキル・経験</h4>
                              <ul className="space-y-2">
                                {job.preferred.map((pref, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm">
                                    <CheckCircle2 className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <span className="text-muted-foreground">{pref}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                          <div className="mt-6 flex justify-end">
                            <Button asChild>
                              <Link href={`/recruit/${job.id}`}>
                                詳細を見る
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </Link>
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </FadeUp>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* 選考プロセス */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-center mb-12">選考プロセス</h2>
            
            <div className="space-y-8">
              {[
                {
                  step: '01',
                  title: '書類選考',
                  description: '履歴書・職務経歴書をもとに選考を行います',
                  duration: '3営業日以内に結果をご連絡'
                },
                {
                  step: '02',
                  title: 'カジュアル面談',
                  description: '相互理解を深めるための面談です（オンライン可）',
                  duration: '30-60分'
                },
                {
                  step: '03',
                  title: '一次面接',
                  description: '技術力や経験について詳しくお聞きします',
                  duration: '60分程度'
                },
                {
                  step: '04',
                  title: '最終面接',
                  description: '経営陣との面接です。ビジョンの共有を重視します',
                  duration: '60分程度'
                },
                {
                  step: '05',
                  title: '内定・条件提示',
                  description: '双方合意のもと、入社条件を決定します',
                  duration: '最終面接から1週間以内'
                }
              ].map((process, index) => (
                <FadeUp delay={index * 0.1}
                  key={index}
                  className="flex gap-6"
                >
                  <div className="shrink-0">
                    <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center font-bold">
                      {process.step}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">{process.title}</h3>
                    <p className="text-muted-foreground mb-1">{process.description}</p>
                    <Badge variant="secondary">{process.duration}</Badge>
                  </div>
                </FadeUp>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* メンバーの声 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">メンバーの声</h2>
            <p className="text-lg text-muted-foreground">
              CodeFoxで働く仲間たちのリアルな声
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: '山田 太郎',
                role: 'フロントエンドエンジニア',
                joined: '2022年入社',
                quote: '地域の課題を技術で解決できることにやりがいを感じています。リモートワークでも密なコミュニケーションが取れる環境です。'
              },
              {
                name: '佐藤 花子',
                role: 'DXコンサルタント',
                joined: '2021年入社',
                quote: '自治体の方々と直接対話し、本当に必要とされるシステムを作れる。概念だけでなく実装まで関われるのが魅力です。'
              },
              {
                name: '鈴木 次郎',
                role: 'AIエンジニア',
                joined: '2023年入社',
                quote: '最新技術を地域課題に応用する挑戦的な環境。技術者として成長しながら、社会貢献できることが嬉しいです。'
              }
            ].map((member, index) => (
              <FadeUp delay={index * 0.1}
                key={index}
              >
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
                      <div>
                        <CardTitle className="text-lg">{member.name}</CardTitle>
                        <CardDescription>
                          {member.role} / {member.joined}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground italic">
                      &ldquo;{member.quote}&rdquo;
                    </p>
                  </CardContent>
                </Card>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-primary to-primary/80 text-white">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center"
          >
            <Building2 className="h-16 w-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl font-bold mb-4">
              あなたの力を、地域のために
            </h2>
            <p className="text-xl mb-8 opacity-90">
              まずはカジュアルにお話ししませんか？<br />
              オンラインでの面談も可能です。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link href="/recruit/entry">
                  エントリーする
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                <Link href="/contact">
                  <Calendar className="mr-2 h-4 w-4" />
                  カジュアル面談を申し込む
                </Link>
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  )
}